import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { useRbac } from './RbacContext';
import { ROLES } from './roles';

const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function _preservedDest(newWsId) {
  try {
    const { pathname, search } = window.location;
    const crmMatch = pathname.match(/^\/app\/[^/]+(\/.*)?$/);
    if (crmMatch) {
      const subPath = crmMatch[1] || '';
      const segments = subPath.split('/').filter(Boolean);
      const safeSegs = [];
      for (const seg of segments) {
        if (_UUID_RE.test(seg)) break; // resource ID — stop here
        safeSegs.push(seg);
      }
      const safeSub = safeSegs.length ? '/' + safeSegs.join('/') : '';
      // Preserve query params only for /settings (section + entity tab are safe; cfFilters are not)
      const preservedSearch = safeSub === '/settings' ? search : '';
      return '/app/' + newWsId + safeSub + preservedSearch;
    }
    // Workspace Admin paths: preserve the sub-path under /workspaces/:wsId
    const wsAdminMatch = pathname.match(/^\/workspaces\/[^/]+(\/.*)?$/);
    if (wsAdminMatch) {
      const subPath = wsAdminMatch[1] || '';
      return '/workspaces/' + newWsId + subPath;
    }
    return '/app/' + newWsId;
  } catch (_) {
    return '/app/' + newWsId;
  }
}

export default function WorkspaceSwitcher({ data, dark, onPicked }) {
  const { workspaceId, setWorkspace, orgId, role, can } = useRbac();
  const auth = window.useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const all    = data.state.workspaces;
  // B-05: only ACTIVE workspaces are eligible switch targets
  const scoped = (role === ROLES.SUPER_ADMIN ? all : all.filter(w => w.org === orgId))
    .filter(w => w.status === 'ACTIVE');
  const active = all.find(w => w.id === workspaceId) || scoped[0];
  const activeOrg = active ? data.state.organizations.find(o => o.id === active.org) : null;

  const pick = async (w) => {
    setOpen(false);
    const dest = _preservedDest(w.id);
    if (auth?.switchWorkspace) {
      try {
        /* WS-004: await backend BEFORE any local state update; nx-session-changed drives RbacContext */
        await auth.switchWorkspace(w.id);
        if (onPicked) onPicked(w);
        navigate(dest); /* WS-005: preserve route context; falls back to home when path has resource IDs */
      } catch { /* nx-session-changed not fired on error — rbac state unchanged */ }
    } else {
      setWorkspace(w.id);
      if (onPicked) onPicked(w);
    }
  };

  if (!active) return null;

  const others = scoped.filter(w => w.id !== active.id);
  const canWsAdmin = can('workspace.settings') && workspaceId;
  const canOrgAdmin = can('org.view') && orgId;
  const canPlatform = can('platform.view');

  return (
    <div ref={ref} className={'nx-ws-switch' + (dark ? ' is-dark' : '')} style={{ position: 'relative' }}>

      {/* ── Trigger ── */}
      <button className="nx-ws-switch-btn" onClick={() => setOpen(o => !o)} title="Switch workspace">
        <span className="nx-ws-switch-mark">{active.name[0].toUpperCase()}</span>
        <span className="nx-ws-switch-meta">
          <span className="nx-ws-switch-name">{active.name}</span>
          <span className="nx-ws-switch-sub">{activeOrg ? activeOrg.name : ''}</span>
        </span>
        <NexusIcon name="ChevronsUpDown" size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
      </button>

      {open && (
        <div className="nx-chip-menu nx-ws-switch-menu">

          {/* Org header */}
          {activeOrg && (
            <div className="nx-ws-org-header">
              <span className="nx-ws-org-mark">{activeOrg.name[0].toUpperCase()}</span>
              <span className="nx-ws-org-name">{activeOrg.name}</span>
              <span className="nx-ws-org-badge">Org</span>
            </div>
          )}

          {/* Active workspace */}
          <div className="nx-ws-switch-sect">Active Workspace</div>
          <button className="nx-ws-item is-active" onClick={() => pick(active)}>
            <span className="nx-ws-item-av">{active.name[0].toUpperCase()}</span>
            <span className="nx-ws-item-meta">
              <span className="nx-ws-item-name">{active.name}</span>
              <span className="nx-ws-item-sub">{active.members} members · {active.modules.length} modules</span>
            </span>
            <NexusIcon name="Check" size={12} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
          </button>

          {/* Other workspaces */}
          {others.length > 0 && (
            <>
              <div style={{ height: 1, background: 'var(--nx-divider)', margin: '4px 0' }} />
              <div className="nx-ws-switch-sect">Switch To</div>
              <div style={{ maxHeight: 168, overflowY: 'auto' }}>
                {others.map(w => (
                  <button key={w.id} className="nx-ws-item" onClick={() => pick(w)}>
                    <span className="nx-ws-item-av">{w.name[0].toUpperCase()}</span>
                    <span className="nx-ws-item-meta">
                      <span className="nx-ws-item-name">{w.name}</span>
                      <span className="nx-ws-item-sub">{w.members} members</span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="nx-ws-switch-footer">
            <button className="nx-ws-act is-accent" onClick={() => { setOpen(false); navigate('/workspaces'); }}>
              <NexusIcon name="LayoutGrid" size={12} /> Browse all workspaces
            </button>
            {(canWsAdmin || canOrgAdmin || canPlatform) && (
              <>
                <div style={{ height: 1, background: 'var(--nx-divider)', margin: '4px 0' }} />
                {canWsAdmin && (
                  <button className="nx-ws-act" onClick={() => { setOpen(false); navigate('/workspaces/' + workspaceId); }}>
                    <NexusIcon name="Settings" size={12} /> Workspace Admin
                  </button>
                )}
                {canOrgAdmin && (
                  <button className="nx-ws-act" onClick={() => { setOpen(false); navigate('/organization/' + orgId, { state: { from: 'crm' } }); }}>
                    <NexusIcon name="Building2" size={12} /> Organization Console
                  </button>
                )}
                {canPlatform && (
                  <button className="nx-ws-act" onClick={() => { setOpen(false); navigate('/platform'); }}>
                    <NexusIcon name="Shield" size={12} /> Control Center
                  </button>
                )}
              </>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
